import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Sparkles, Upload, FileText, Brain, AlertTriangle, Check, Loader2, Download, Cable, Target,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── AI Integration Mapper Dialog ───
export function AiIntegrationMapper({ open, onOpenChange, token }) {
  const [rawText, setRawText] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/map-integrations`, { raw_text: rawText, vendor_name: vendorName }, { headers });
      setResults(res.data.integrations || []);
      toast.success(`Mapped ${(res.data.integrations || []).length} integrations`);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-500" /> AI Integration Mapper</DialogTitle>
          <DialogDescription>Paste a vendor's integration list and AI will map each one to your platform types.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Vendor Name</Label><Input value={vendorName} onChange={e => setVendorName(e.target.value)} placeholder="e.g., Toast, Oracle, StayNTouch" /></div>
          <div><Label className="text-xs">Integration Text (paste from website, PDF, or spreadsheet)</Label><Textarea value={rawText} onChange={e => setRawText(e.target.value)} rows={6} placeholder="Paste the vendor's integration list here..." /></div>
        </div>
        {results && (
          <ScrollArea className="flex-1 max-h-[300px] border rounded-lg">
            <div className="p-3 space-y-1.5">
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 text-xs">
                  <Cable className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                  <span className="font-medium flex-1">{r.integration_name}</span>
                  <Badge variant="outline" className="text-[9px]">{r.platform_name}</Badge>
                  <Badge className={`text-[9px] ${r.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.confidence}</Badge>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={analyze} disabled={loading || !rawText.trim()} className="gap-1.5">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {loading ? 'Analyzing...' : 'Map Integrations'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AI Tech Advisor Dialog ───
export function AiTechAdvisor({ open, onOpenChange, token, propertyId, propertyName }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/tech-advisor`, { property_id: propertyId }, { headers });
      setResult(res.data);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Brain className="h-4 w-4 text-purple-500" /> AI Tech Stack Advisor</DialogTitle>
          <DialogDescription>AI analyzes {propertyName}'s attributes and recommends missing platforms.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="py-8 text-center">
            <Button onClick={analyze} disabled={loading} className="gap-2" size="lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
              {loading ? 'Analyzing your stack...' : 'Get AI Recommendations'}
            </Button>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="space-y-4 py-2">
              {result.summary && <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">{result.summary}</p>}
              {(result.recommendations || []).map((r, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${r.priority === 'high' ? 'border-l-4 border-l-red-400' : r.priority === 'medium' ? 'border-l-4 border-l-amber-300' : 'border-l-4 border-l-blue-200'}`}>
                  <Target className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><span className="text-sm font-semibold">{r.platform_name || r.platform_id}</span><Badge className={`text-[9px] ${r.priority === 'high' ? 'bg-red-100 text-red-700' : r.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{r.priority}</Badge></div>
                    <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        <DialogFooter><Button variant="ghost" onClick={() => { onOpenChange(false); setResult(null); }}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AI Contract Analyzer Dialog ───
export function AiContractAnalyzer({ open, onOpenChange, token }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/analyze-contract`, { contract_text: text }, { headers });
      setResult(res.data);
      toast.success('Contract analyzed');
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-emerald-500" /> AI Contract Analyzer</DialogTitle>
          <DialogDescription>Paste contract text and AI extracts key terms, dates, and risks.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3 py-2">
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={8} placeholder="Paste contract text here..." />
            <Button onClick={analyze} disabled={loading || !text.trim()} className="w-full gap-1.5">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {loading ? 'Analyzing contract...' : 'Extract Key Terms'}
            </Button>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="space-y-3 py-2 text-sm">
              {result.vendor_name && <div className="flex justify-between"><span className="text-muted-foreground">Vendor</span><span className="font-medium">{result.vendor_name}</span></div>}
              {result.annual_value > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Annual Value</span><span className="font-bold text-primary">${result.annual_value?.toLocaleString()}</span></div>}
              {result.start_date && <div className="flex justify-between"><span className="text-muted-foreground">Start Date</span><span>{result.start_date}</span></div>}
              {result.end_date && <div className="flex justify-between"><span className="text-muted-foreground">End Date</span><span>{result.end_date}</span></div>}
              {result.renewal_date && <div className="flex justify-between"><span className="text-muted-foreground">Renewal</span><span className="text-amber-600 font-medium">{result.renewal_date}</span></div>}
              {result.auto_renew !== undefined && <div className="flex justify-between"><span className="text-muted-foreground">Auto-Renew</span><Badge className={result.auto_renew ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{result.auto_renew ? 'Yes' : 'No'}</Badge></div>}
              {result.pricing_model && <div><span className="text-muted-foreground text-xs">Pricing Model</span><p className="mt-0.5">{result.pricing_model}</p></div>}
              {result.sla_terms && <div><span className="text-muted-foreground text-xs">SLA Terms</span><p className="mt-0.5">{result.sla_terms}</p></div>}
              {(result.risks || []).length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-red-600 uppercase">Risks & Concerns</span>
                  <div className="mt-1 space-y-1">{result.risks.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs p-2 rounded bg-red-50 text-red-700"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{r}</div>
                  ))}</div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
        <DialogFooter><Button variant="ghost" onClick={() => { onOpenChange(false); setResult(null); setText(''); }}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AI Anomaly Detection Panel ───
export function AiAnomalyPanel({ token }) {
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const detect = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/anomalies`, { headers });
      setAnomalies(res.data.anomalies || []);
    } catch { toast.error('Detection failed'); }
    finally { setLoading(false); }
  };

  const typeColors = { risk: 'bg-red-100 text-red-700 border-l-red-400', opportunity: 'bg-emerald-100 text-emerald-700 border-l-emerald-400', warning: 'bg-amber-100 text-amber-700 border-l-amber-400' };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="ai-anomalies">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Brain className="h-5 w-5 text-purple-500" /> AI Anomaly Detection</h2>
          <p className="text-sm text-muted-foreground">AI scans your portfolio for unusual patterns, risks, and opportunities.</p>
        </div>
        <Button onClick={detect} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {loading ? 'Scanning...' : 'Run AI Scan'}
        </Button>
      </div>
      {anomalies === null ? (
        <div className="py-12 text-center border rounded-xl">
          <Brain className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Click "Run AI Scan" to analyze your portfolio for anomalies.</p>
        </div>
      ) : anomalies.length === 0 ? (
        <div className="py-12 text-center border rounded-xl">
          <Check className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No anomalies detected. Your portfolio looks healthy!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {anomalies.map((a, i) => (
            <div key={i} className={`p-4 rounded-lg border border-l-4 ${typeColors[a.type] || typeColors.warning}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold">{a.property}</span>
                <Badge className={`text-[9px] ${typeColors[a.type]?.split(' ').slice(0, 2).join(' ') || ''}`}>{a.type}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{a.description}</p>
              <p className="text-xs text-primary mt-1 font-medium">{a.recommendation}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── AI Onboarding Chat ───
export function AiOnboardingChat({ token }) {
  const [messages, setMessages] = useState([{ role: 'ai', text: 'Hi! I\'m your LodgiFlow onboarding assistant. Tell me about your property and I\'ll help you get set up. What type of property is it?' }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/onboarding-assistant`, { message: userMsg, context: { session: sessionId } }, { headers });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch { setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I had trouble processing that. Could you try again?' }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="ai-onboarding-chat">
      <div className="flex items-center gap-2 mb-2">
        <Brain className="h-5 w-5 text-purple-500" />
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>AI Onboarding Assistant</h2>
      </div>
      <ScrollArea className="h-[400px] border rounded-xl p-4">
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-xl text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="p-3 rounded-xl bg-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
        </div>
      </ScrollArea>
      <div className="flex gap-2">
        <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Describe your property..." className="flex-1" />
        <Button onClick={send} disabled={loading || !input.trim()} className="gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Send</Button>
      </div>
    </div>
  );
}

// ─── AI Weekly Digest ───
export function AiWeeklyDigest({ token }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/weekly-digest`, {}, { headers });
      setDigest(res.data);
    } catch { toast.error('Failed to generate digest'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="ai-weekly-digest">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Sparkles className="h-5 w-5 text-amber-500" /> AI Weekly Digest</h2>
          <p className="text-sm text-muted-foreground">AI-generated portfolio summary with action items.</p>
        </div>
        <Button onClick={generate} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {loading ? 'Generating...' : 'Generate Digest'}
        </Button>
      </div>
      {digest && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl border bg-card text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{digest.metrics?.properties}</div><div className="text-[10px] text-muted-foreground">Properties</div></div>
            <div className="p-3 rounded-xl border bg-card text-center"><div className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{digest.metrics?.assignments}</div><div className="text-[10px] text-muted-foreground">Solutions</div></div>
            <div className="p-3 rounded-xl border bg-card text-center"><div className="text-xl font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk' }}>{digest.metrics?.upcoming_renewals}</div><div className="text-[10px] text-muted-foreground">Renewals (30d)</div></div>
            <div className="p-3 rounded-xl border bg-card text-center"><div className="text-xl font-bold text-red-500" style={{ fontFamily: 'Space Grotesk' }}>{digest.metrics?.no_governance}</div><div className="text-[10px] text-muted-foreground">No Governance</div></div>
          </div>
          <div className="p-4 rounded-xl border bg-card whitespace-pre-wrap text-sm leading-relaxed">{digest.digest}</div>
        </div>
      )}
    </div>
  );
}

// ─── AI Natural Language Search ───
export function AiNaturalSearch({ token, onNavigateReport }) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/natural-query`, { query: query.trim() }, { headers });
      setResult(res.data);
    } catch { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="ai-natural-search">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Brain className="h-5 w-5 text-blue-500" /> Ask LodgiFlow</h2>
      <p className="text-sm text-muted-foreground">Ask a question in plain English and AI will find the right report.</p>
      <div className="flex gap-2">
        <Input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} placeholder='e.g., "Which properties have the worst SSO coverage?"' className="flex-1" />
        <Button onClick={search} disabled={loading} className="gap-1.5">{loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />} Ask</Button>
      </div>
      {result && (
        <div className="p-4 rounded-xl border bg-card space-y-3">
          <p className="text-sm">{result.explanation}</p>
          {result.report_id && (
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => onNavigateReport?.(result.report_id)}>
              <Target className="h-3 w-3" /> Open {result.report_id.replace(/-/g, ' ')}
            </Button>
          )}
          {(result.alternative_reports || []).length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              <span className="text-xs text-muted-foreground">Also try:</span>
              {result.alternative_reports.map(r => <Badge key={r} variant="outline" className="text-[9px] cursor-pointer hover:bg-muted" onClick={() => onNavigateReport?.(r)}>{r}</Badge>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── AI Integration Path Finder ───
export function AiPathFinder({ token }) {
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [targetPlatform, setTargetPlatform] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch properties and platforms on mount
  React.useEffect(() => {
    const load = async () => {
      try {
        const [propRes, stackRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/properties`, { headers }),
          axios.get(`${BACKEND_URL}/api/stack`),
        ]);
        setProperties(propRes.data.properties || []);
        setPlatforms((stackRes.data.nodes || []).map(n => ({ id: n.id, name: n.name, short: n.short })));
      } catch { /* ignore */ }
    };
    load();
  }, [token]); // eslint-disable-line

  const analyze = async () => {
    if (!selectedProperty) { toast.error('Select a property first'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/integration-pathfinder`, {
        property_id: selectedProperty,
        target_platform_id: targetPlatform || undefined,
      }, { headers });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Path finder failed');
    } finally { setLoading(false); }
  };

  const priorityColors = {
    Critical: 'bg-red-100 text-red-700 border-l-red-500',
    High: 'bg-amber-100 text-amber-700 border-l-amber-500',
    Medium: 'bg-blue-100 text-blue-700 border-l-blue-400',
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="ai-pathfinder">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <Cable className="h-5 w-5 text-emerald-500" /> AI Integration Path Finder
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Analyze a property's tech stack and discover optimal integration paths between systems.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Property</Label>
          <select
            value={selectedProperty}
            onChange={e => setSelectedProperty(e.target.value)}
            className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
            data-testid="pathfinder-property-select"
          >
            <option value="">Select a property...</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium">Target Platform (optional)</Label>
          <select
            value={targetPlatform}
            onChange={e => setTargetPlatform(e.target.value)}
            className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
            data-testid="pathfinder-platform-select"
          >
            <option value="">Any / All integrations</option>
            {platforms.map(p => <option key={p.id} value={p.id}>{p.short || p.name}</option>)}
          </select>
        </div>
      </div>

      <Button onClick={analyze} disabled={loading || !selectedProperty} className="gap-2" data-testid="pathfinder-run-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cable className="h-4 w-4" />}
        {loading ? 'Analyzing integration paths...' : 'Find Integration Paths'}
      </Button>

      {result && (
        <div className="space-y-4 animate-fade-in">
          {result.summary && (
            <div className="p-4 rounded-xl border bg-muted/30 text-sm leading-relaxed">
              {result.summary}
            </div>
          )}

          {(result.recommendations || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Recommended Integrations</h3>
              <div className="space-y-2">
                {result.recommendations.map((r, i) => (
                  <div key={i} className={`p-3 rounded-lg border border-l-4 text-sm ${priorityColors[r.priority] || priorityColors.Medium}`}
                    data-testid={`pathfinder-rec-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{r.from} → {r.to}</span>
                      <Badge className="text-[9px]">{r.priority}</Badge>
                      {r.vendor_supported && <Badge variant="outline" className="text-[9px] text-emerald-600 border-emerald-300">Vendor API</Badge>}
                    </div>
                    <p className="text-xs opacity-80">{r.data_flow}</p>
                    <p className="text-xs mt-1 font-medium">{r.business_value}</p>
                    {r.implementation_note && <p className="text-[11px] mt-1 opacity-60">{r.implementation_note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(result.integration_sequence || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Implementation Sequence</h3>
              <div className="space-y-1.5">
                {result.integration_sequence.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(result.gaps_identified || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-600 mb-2">Gaps Identified</h3>
              <div className="space-y-1">
                {result.gaps_identified.map((gap, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs p-2 rounded bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                    <span>{gap}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── AI Integration Mapper Panel (standalone) ───
export function AiIntegrationMapperPanel({ token }) {
  const [rawText, setRawText] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/map-integrations`, { raw_text: rawText, vendor_name: vendorName }, { headers });
      setResults(res.data.integrations || []);
      toast.success(`Mapped ${(res.data.integrations || []).length} integrations`);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-integration-mapper-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Sparkles className="h-5 w-5 text-blue-500" /> AI Integration Mapper</h2>
      <p className="text-sm text-muted-foreground">Paste a vendor&apos;s integration list and AI will map each one to your platform types.</p>
      <div className="space-y-3">
        <Input value={vendorName} onChange={e => setVendorName(e.target.value)} placeholder="Vendor name (e.g., Oracle, Toast)" />
        <Textarea value={rawText} onChange={e => setRawText(e.target.value)} rows={5} placeholder="Paste the vendor's integration list here..." />
        <Button onClick={analyze} disabled={loading || !rawText.trim()} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {loading ? 'Mapping...' : 'Map Integrations'}
        </Button>
      </div>
      {results && (
        <div className="space-y-1.5 border rounded-lg p-3">
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 text-xs">
              <Cable className="h-3.5 w-3.5 text-blue-500 shrink-0" />
              <span className="font-medium flex-1">{r.integration_name}</span>
              <Badge variant="outline" className="text-[9px]">{r.platform_name}</Badge>
              <Badge className={`text-[9px] ${r.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.confidence}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AI Tech Advisor Panel ───
export function AiTechAdvisorPanel({ token }) {
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/properties`, { headers }).then(r => setProperties(r.data?.properties || r.data || [])).catch(() => {});
  }, [token]); // eslint-disable-line

  const analyze = async () => {
    if (!selectedProp) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/tech-advisor`, { property_id: selectedProp }, { headers });
      setResult(res.data);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-tech-advisor-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Brain className="h-5 w-5 text-purple-500" /> AI Tech Advisor</h2>
      <p className="text-sm text-muted-foreground">Select a property and AI recommends missing platforms based on its attributes.</p>
      <div className="flex gap-2">
        <select value={selectedProp} onChange={e => setSelectedProp(e.target.value)} className="flex-1 rounded-md border bg-background px-3 py-2 text-sm">
          <option value="">Select property...</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button onClick={analyze} disabled={loading || !selectedProp} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />} Analyze
        </Button>
      </div>
      {result && (
        <div className="space-y-3">
          {result.summary && <div className="p-3 rounded-lg bg-muted/30 text-sm">{result.summary}</div>}
          {(result.recommendations || []).map((r, i) => (
            <div key={i} className={`p-3 rounded-lg border border-l-4 text-sm ${r.priority === 'high' ? 'border-l-red-400' : r.priority === 'medium' ? 'border-l-amber-300' : 'border-l-blue-200'}`}>
              <div className="flex items-center gap-2 mb-1"><span className="font-semibold">{r.platform_name || r.platform_id}</span><Badge className="text-[9px]">{r.priority}</Badge></div>
              <p className="text-xs text-muted-foreground">{r.reason}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AI Contract Analyzer Panel ───
export function AiContractAnalyzerPanel({ token }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/analyze-contract`, { contract_text: text }, { headers });
      setResult(res.data);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-contract-analyzer-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><FileText className="h-5 w-5 text-emerald-500" /> AI Contract Analyzer</h2>
      <p className="text-sm text-muted-foreground">Paste contract text and AI extracts key terms, dates, pricing, and risks.</p>
      <Textarea value={text} onChange={e => setText(e.target.value)} rows={6} placeholder="Paste contract text here..." />
      <Button onClick={analyze} disabled={loading || !text.trim()} className="gap-1.5">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Extract Terms
      </Button>
      {result && (
        <div className="space-y-2 text-sm border rounded-lg p-4">
          {result.vendor_name && <div className="flex justify-between"><span className="text-muted-foreground">Vendor</span><span className="font-medium">{result.vendor_name}</span></div>}
          {result.annual_value > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Annual Value</span><span className="font-bold text-primary">${result.annual_value?.toLocaleString()}</span></div>}
          {result.start_date && <div className="flex justify-between"><span className="text-muted-foreground">Start</span><span>{result.start_date}</span></div>}
          {result.end_date && <div className="flex justify-between"><span className="text-muted-foreground">End</span><span>{result.end_date}</span></div>}
          {result.renewal_date && <div className="flex justify-between"><span className="text-muted-foreground">Renewal</span><span className="text-amber-600">{result.renewal_date}</span></div>}
          {(result.risks || []).length > 0 && <div className="mt-2"><span className="text-xs font-semibold text-red-600">Risks</span>{result.risks.map((r, i) => <div key={i} className="text-xs p-1.5 mt-1 rounded bg-red-50 text-red-700"><AlertTriangle className="h-3 w-3 inline mr-1" />{r}</div>)}</div>}
        </div>
      )}
    </div>
  );
}

// ─── AI RFP Writer Panel ───
export function AiRfpWriterPanel({ token }) {
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [selectedPlat, setSelectedPlat] = useState('');
  const [requirements, setRequirements] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      axios.get(`${BACKEND_URL}/api/stack`),
    ]).then(([pRes, sRes]) => {
      setProperties(pRes.data?.properties || pRes.data || []);
      setPlatforms((sRes.data.nodes || []).map(n => ({ id: n.id, name: n.name })));
    }).catch(() => {});
  }, [token]); // eslint-disable-line

  const generate = async () => {
    if (!selectedProp || !selectedPlat) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/generate-rfp`, { property_id: selectedProp, platform_type_id: selectedPlat, additional_requirements: requirements }, { headers });
      setResult(res.data);
    } catch { toast.error('Generation failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-rfp-writer-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><FileText className="h-5 w-5 text-indigo-500" /> AI RFP Writer</h2>
      <p className="text-sm text-muted-foreground">Generate a professional RFP document tailored to your property and platform needs.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Property</Label><select value={selectedProp} onChange={e => setSelectedProp(e.target.value)} className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"><option value="">Select...</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div><Label className="text-xs">Platform</Label><select value={selectedPlat} onChange={e => setSelectedPlat(e.target.value)} className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"><option value="">Select...</option>{platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
      </div>
      <Textarea value={requirements} onChange={e => setRequirements(e.target.value)} rows={3} placeholder="Additional requirements (optional)..." />
      <Button onClick={generate} disabled={loading || !selectedProp || !selectedPlat} className="gap-1.5">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate RFP
      </Button>
      {result?.rfp_content && <div className="p-4 rounded-lg border bg-card whitespace-pre-wrap text-sm leading-relaxed max-h-[500px] overflow-y-auto">{result.rfp_content}</div>}
    </div>
  );
}

// ─── AI Vendor Compare Panel ───
export function AiVendorComparePanel({ token }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/products?limit=200`, { headers }).then(r => setProducts(r.data?.products || [])).catch(() => {});
  }, [token]); // eslint-disable-line

  const compare = async () => {
    if (selected.length < 2) { toast.error('Select at least 2 products'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/vendor-compare`, { product_ids: selected }, { headers });
      setResult(res.data);
    } catch { toast.error('Comparison failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-vendor-compare-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Target className="h-5 w-5 text-orange-500" /> AI Vendor Compare</h2>
      <p className="text-sm text-muted-foreground">Select 2-3 products and AI compares them with strengths, weaknesses, and recommendations.</p>
      <div className="flex flex-wrap gap-1 mb-2">{selected.map(id => { const p = products.find(x => x.id === id); return <Badge key={id} className="text-[9px] gap-1 cursor-pointer" onClick={() => setSelected(s => s.filter(x => x !== id))}>{p?.name || id} &times;</Badge>; })}</div>
      <select onChange={e => { if (e.target.value && !selected.includes(e.target.value) && selected.length < 3) setSelected(s => [...s, e.target.value]); e.target.value = ''; }} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
        <option value="">Add product to compare...</option>
        {products.filter(p => !selected.includes(p.id)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <Button onClick={compare} disabled={loading || selected.length < 2} className="gap-1.5">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />} Compare
      </Button>
      {result && (
        <div className="p-4 rounded-lg border bg-card space-y-3 text-sm">
          {result.recommendation && <p className="font-medium">{result.recommendation}</p>}
          {result.summary && <p className="text-muted-foreground">{result.summary}</p>}
        </div>
      )}
    </div>
  );
}

// ─── AI Cost Optimizer Panel ───
export function AiCostOptimizerPanel({ token }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const optimize = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/cost-optimizer`, {}, { headers });
      setResult(res.data);
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-cost-optimizer-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Sparkles className="h-5 w-5 text-emerald-500" /> AI Cost Optimizer</h2>
          <p className="text-sm text-muted-foreground">AI identifies vendor consolidation opportunities and estimated savings.</p>
        </div>
        <Button onClick={optimize} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Find Savings
        </Button>
      </div>
      {result ? (
        <div className="space-y-3">
          {result.summary && <div className="p-3 rounded-lg bg-muted/30 text-sm">{result.summary}</div>}
          {result.total_estimated_savings && <div className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>Potential savings: {result.total_estimated_savings}</div>}
          {(result.opportunities || []).map((o, i) => (
            <div key={i} className="p-3 rounded-lg border text-sm">
              <div className="flex items-center gap-2 mb-1"><span className="font-semibold">{o.platform}</span><Badge variant="outline" className="text-[8px]">{o.effort} effort</Badge></div>
              <p className="text-xs text-muted-foreground">{o.current_state}</p>
              <p className="text-xs font-medium mt-1">{o.recommendation}</p>
              {o.estimated_savings && <p className="text-xs text-emerald-600 font-bold mt-1">{o.estimated_savings}</p>}
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded-xl p-12 text-center"><Sparkles className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Click &ldquo;Find Savings&rdquo; to analyze vendor fragmentation across your portfolio.</p></div>
      )}
    </div>
  );
}

// ─── AI Benchmarks Panel ───
export function AiBenchmarksPanel({ token }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/benchmarks`, { headers });
      setResult(res.data);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-benchmarks-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Target className="h-5 w-5 text-blue-500" /> AI Benchmark Engine</h2>
          <p className="text-sm text-muted-foreground">Compare your portfolio against anonymized industry benchmarks.</p>
        </div>
        <Button onClick={run} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />} Run Benchmarks
        </Button>
      </div>
      {result ? (
        <div className="space-y-3">
          {result.overall_assessment && <div className="p-3 rounded-lg bg-muted/30 text-sm">{result.overall_assessment}</div>}
          {(result.benchmarks || []).map((b, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border text-sm">
              <div className="flex-1"><span className="font-semibold">{b.metric}</span><p className="text-xs text-muted-foreground mt-0.5">{b.insight}</p></div>
              <div className="text-right"><div className="font-mono font-bold">{b.portfolio_value}</div><div className="text-[10px] text-muted-foreground">vs {b.industry_avg}</div></div>
              <Badge className={`text-[9px] ${b.rating === 'above' ? 'bg-emerald-100 text-emerald-700' : b.rating === 'below' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{b.rating}</Badge>
            </div>
          ))}
          {(result.top_recommendations || []).length > 0 && <div className="p-3 rounded-lg bg-amber-50 text-sm"><span className="font-semibold text-amber-700">Recommendations</span>{result.top_recommendations.map((r, i) => <p key={i} className="text-xs mt-1">• {r}</p>)}</div>}
        </div>
      ) : (
        <div className="border rounded-xl p-12 text-center"><Target className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Click &ldquo;Run Benchmarks&rdquo; to compare your portfolio against industry averages.</p></div>
      )}
    </div>
  );
}

// ─── AI Predict Integrations Panel ───
export function AiPredictPanel({ token }) {
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/properties`, { headers }).then(r => setProperties(r.data?.properties || r.data || [])).catch(() => {});
  }, [token]); // eslint-disable-line

  const predict = async () => {
    if (!selectedProp) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/predict-integrations?property_id=${selectedProp}`, {}, { headers });
      setResult(res.data);
    } catch { toast.error('Prediction failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="ai-predict-panel">
      <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Brain className="h-5 w-5 text-violet-500" /> AI Integration Predictor</h2>
      <p className="text-sm text-muted-foreground">Select a property and AI predicts which platforms they&apos;re most likely to add next based on similar properties.</p>
      <div className="flex gap-2">
        <select value={selectedProp} onChange={e => setSelectedProp(e.target.value)} className="flex-1 rounded-md border bg-background px-3 py-2 text-sm">
          <option value="">Select property...</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button onClick={predict} disabled={loading || !selectedProp} className="gap-1.5">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />} Predict
        </Button>
      </div>
      {result && (
        <div className="space-y-3">
          {result.summary && <div className="p-3 rounded-lg bg-muted/30 text-sm">{result.summary}</div>}
          {(result.predictions || []).map((p, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border text-sm">
              <span className="font-semibold flex-1">{p.platform}</span>
              <Badge className={`text-[9px] ${p.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : p.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{p.confidence}</Badge>
              <span className="text-xs text-muted-foreground max-w-[200px] truncate">{p.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
